/**
 * Fiche séance n°11 — Permis AM (BSR) — Formation 8h obligatoire (14 ans)
 *
 * Source : notes manuscrites de moniteur ECSR (mai 2026) — pages WA0006, WA0010,
 * WA0014 (débridage), WA0015 (Séquence 5 sensibilisation aux risques).
 *
 * Cadre légal : arrêté du 8 novembre 2012 modifié (formation pratique 8h)
 * + arrêté du 14 avril 2014 + Code de la route (R211-2, R311-1 catégorie L1e/L6e).
 *
 * Public : adolescents 14 ans (cyclomoteur 50 cm³) ou 16 ans (quadricycle L6e).
 * Structure : 8h en 5 séquences réglementaires (arrêté 8 novembre 2012) :
 *   - Séq 1 : Échange sur la conduite (30 min min · ici 50 min pratique)
 *   - Séq 2 : Hors circulation / plateau (1h min · étendu à 3h pour atteindre 8h)
 *   - Séq 3 : Code & savoir-être routier (30 min min · ici 1h)
 *   - Séq 4 : En circulation (3h min)
 *   - Séq 5 : Sensibilisation aux risques (1h min, parent obligatoire si mineur)
 *
 * Officiel (PDF service-public F2890) :
 *   Total minimum 8h, dont 1h théorie (Séq 1+3) + 3h plateau (Séq 2) + 3h
 *   circulation (Séq 4) + 1h sensibilisation parents (Séq 5).
 */

export const permisAmBsrCours = {
  slug: 'permis-am-bsr',
  number: 11,
  title: 'Permis AM (BSR) — Formation 8h obligatoire',
  subtitle:
    "Programme COMPLET de la formation 8h obligatoire (5 séquences) pour le Permis AM cyclomoteur (50 cm³, L1e) à partir de 14 ans, ou quadricycle léger L6e à 16 ans.",
  duration: '8h (5 séquences réglementaires)',
  level: 'Adolescents 14 ans (L1e) / 16 ans (L6e)',
  format: 'Salle (Séq 1+3 = 1h) + Plateau (Séq 2 = 3h) + Circulation (Séq 4 = 3h) + Sensibilisation parents (Séq 5 = 1h)',
  accent: '#7c3aed',
  icon: 'Bike',

  phraseCle:
    "À 14 ans, ce n'est pas seulement le scooter qu'on apprend. C'est la responsabilité d'un véhicule motorisé sur une chaussée partagée avec des camions de 38 tonnes. Notre rôle, c'est d'installer 8 heures un réflexe d'humilité et de visibilité qui peuvent durer toute une vie.",

  source: {
    label: 'Notes moniteur ECSR — Permis AM (arrêté 8 novembre 2012 modifié)',
    pages: 'Cahier moniteur — 4 pages',
  },

  remc: {
    competences: [
      {
        code: 'C1',
        label: 'Maîtriser le maniement du cyclomoteur en circulation à allure réduite',
        souscompetences: [
          "C1.1 — Connaître les commandes (gaz, freins, clignotants, feux, kick / démarreur)",
          "C1.2 — Démarrer, s'arrêter, conserver l'équilibre",
          "C1.3 — Doser l'accélération et le freinage progressif",
          "C1.4 — Diriger le cyclomoteur en utilisant le regard",
        ],
      },
      {
        code: 'C2',
        label: 'Appréhender la route et circuler dans des conditions normales',
        souscompetences: [
          "C2.1 — Rechercher la signalisation et les indices utiles",
          "C2.2 — Positionner le cyclomoteur sur la chaussée",
          "C2.3 — Adapter l'allure aux situations",
          "C2.4 — Franchir intersections et changer de direction",
        ],
      },
      {
        code: 'C4',
        label: 'Pratiquer une conduite autonome, sûre et économique',
        souscompetences: [
          "C4.6 — Connaître les principaux facteurs d'accident",
          "C4.7 — Connaître les comportements à adopter en cas d'accident",
          "C4.10 — Connaître les effets de l'alcool, des stupéfiants, des médicaments",
        ],
      },
    ],
    matrice_gde:
      "Niveaux 2-3-4 — Maîtrise véhicule + contexte conduite + ÉLÉMENTS DE PERSONNALITÉ (très important à 14 ans : pression du groupe, image, prise de risque).",
    ppo: "Pédagogie Par Objectifs adaptée à l'adolescent : verbes simples, démonstration avant verbalisation, débrief court et concret.",
  },

  objectifs: {
    savoir:
      "À la fin de ces 8h, TU SAURAS citer les commandes du cyclomoteur et leur rôle, tu pourras identifier la signalisation prioritaire (STOP, Cédez-le-passage, panneaux danger/interdiction/obligation), et tu sauras expliquer les principaux facteurs d'accident en cyclomoteur (vitesse, débridage, alcool, défaut d'équipement, distraction).",
    savoirFaire:
      "À la fin de ces 8h, TU SAURAS FAIRE le démarrage et l'arrêt en sécurité, tu seras capable de réaliser les exercices plateau (béquillage, freinage, demi-tour, slalom, huit), tu sauras franchir intersections et giratoires en circulation réelle, et tu adapteras ton allure à la visibilité et au comportement des autres usagers.",
    savoirEtre:
      "Au sortir de cette formation, TU ADOPTERAS une posture d'humilité face à la route : EPI systématique (casque attaché, gants, chaussures hautes), respect strict du Code, refus du défi avec les copains, conscience que ta visibilité est minime (1 m² frontal). Tu seras capable de refuser un comportement à risque proposé par un pair.",
  },

  prerequis: [
    'Âge : 14 ans révolus (L1e cyclomoteur) ou 16 ans (L6e quadricycle léger)',
    "Attestation de Sécurité Routière (ASSR1) — collège classe de 5e — ou ASR (adultes)",
    "Livret d'apprentissage signé par le représentant légal (tuteur) — vérifier signature à l'accueil",
    "EPI complet OBLIGATOIRE : casque homologué attaché, gants homologués, chaussures montantes, vêtements adaptés (veste manches longues, pantalon)",
    'Présence souhaitée des parents sur la Séquence 5 (sensibilisation aux risques)',
  ],

  supports: [
    "Livret d'apprentissage Permis AM (référentiel REMC)",
    "Scooter ou cyclo 50 cm³ homologué (vérification kick / démarreur)",
    "Plateau homologué (cônes, ligne d'arrêt, repères 4 et 13 pas)",
    "Panneaux pédagogiques pour Séq 3 (formes / couleurs / catégories)",
    "Vidéo/photos accidentologie cyclo 14-17 ans (ONISR 2024)",
    "Fiche débridage (135 € amende, +30 % accident, perte assurance)",
    "Attestation de fin de formation (valable IMMÉDIATEMENT pendant 4 mois en attendant le titre)",
  ],

  deroule: [
    {
      etape: 1,
      duree: '50 min',
      titre: 'Séquence 1 — Accueil & échange sur la conduite',
      role: 'Animateur',
      animation:
        "Bienvenue à toi et à tes parents. Je vais vous présenter le programme des 2 demi-journées (8h au total). On va d'abord vérifier ton livret d'apprentissage : la signature du tuteur doit être bien présente. Puis ton équipement : casque homologué et bien ATTACHÉ, gants homologués, chaussures hautes, veste et vêtements adaptés. Tour de table : prénom, classe, expérience antérieure (vélo, scooter en famille, ou rien du tout), tes attentes. Ensuite on échange sur ta vision du scooter : « qu'est-ce que c'est pour toi un scooter ? un objet de liberté ? de statut ? un outil ? ». Ne reste pas dans des représentations héroïques — on va construire ensemble une vision réaliste.",
      questions_eleve: [
        "Pourquoi as-tu choisi de passer ton AM ?",
        "Que fais-tu si tu vois un copain rouler sans casque ?",
        "Est-ce que tu trouves « cool » un scooter débridé ?",
      ],
      attendu:
        "Climat de confiance, équipement validé, tu sais citer 3 EPI obligatoires et tu comprends pourquoi le casque doit être ATTACHÉ (pas juste posé).",
    },
    {
      etape: 2,
      duree: '3h',
      titre: 'Séquence 2 — Maniabilité hors circulation (plateau)',
      role: 'Pratique encadrée',
      animation:
        "On commence par découvrir ton scooter et ses commandes : guidon, freins (droite = avant / gauche = arrière sur 2RM standard, MAIS INVERSE sur scooter à variateur où gauche = arrière), clignotants, feux, feux de détresse. Démarrage : kick OU démarreur électrique selon ton modèle. On enchaîne les exercices. ① Béquillage / dé-béquillage (centrale + latérale, attention aux obstacles autour de toi). ② Démarrage-arrêt avec position de conduite : tête levée, genoux serrés. ③ Slaloms lents entre cônes. ④ Demi-tours (rayon de plus en plus serré). ⑤ Huit (lemniscate). ⑥ Huit avec exercice du cône : regard 1 cône d'avance. Je te le répéterai 100 fois : LA TÊTE TOURNE AVANT LE GUIDON.",
      questions_eleve: [
        "Quelle est la commande la plus importante sur ton scooter ? Pourquoi ?",
        "Où poses-tu ton regard en slalom ? Et en demi-tour ?",
        "Pourquoi 4 doigts sur le levier de frein et pas 2 ?",
      ],
      attendu:
        "Tu deviens FLUIDE sur 70 % des passages (cible plus basse qu'en B→125 car tu es débutant total), zéro chute, tu sais citer les 4 commandes principales.",
    },
    {
      etape: 3,
      duree: '1h',
      titre: 'Séquence 3 — Code de la route & savoir-être routier',
      role: 'Apport théorique',
      animation:
        "On va découvrir la signalisation routière. (1) Verticale (les panneaux) : Danger (triangle rouge), Interdiction (rond rouge), Obligation (rond bleu), Indication (carré bleu), Direction (rectangle), Localisation. (2) Horizontale (le marquage au sol). Tu vas apprendre à les catégoriser par forme et couleur. La hiérarchie des priorités, dans l'ordre : (1) Forces de l'ordre, (2) Feux tricolores, (3) Panneaux STOP / Cédez-le-passage, (4) Priorité à droite, (5) Marquage au sol. Pour franchir une intersection, retiens la règle Gauche-Droite-Gauche. Le « coussin de sécurité » : se décaler pour mieux voir. Ton allure doit toujours s'adapter à 3 choses : la visibilité, l'état de la chaussée, ta propre capacité de freinage.",
      questions_eleve: [
        "De quelle couleur est un panneau d'interdiction ?",
        "Au STOP, regarde-t-on d'abord à gauche ou à droite ?",
        "Que veut dire « coussin de sécurité » ?",
      ],
      attendu:
        "À la fin de cette étape, tu SAURAS classer 10 panneaux par catégorie (forme + couleur), tu pourras citer la hiérarchie des priorités et tu sauras expliquer le coussin de sécurité.",
    },
    {
      etape: 4,
      duree: '3h',
      titre: 'Séquence 4 — Conduite en circulation réelle',
      role: 'Pratique en suivi',
      animation:
        "Avant de partir : check EPI complet, check équipement, briefing intercom ou signaux convenus (je te suivrai en cyclo, à vélo ou à pied — pas obligatoirement en voiture suiveuse pour le AM). Objectifs progressifs : (1) Démarrage en circulation basse densité (zone 30, lotissement). (2) Intersections avec priorité droite. (3) STOP et Cédez-le-passage. (4) Mini-giratoire urbain. (5) Croisement avec usagers vulnérables (piétons, cyclistes, autres scooters). (6) Conduite sous éclairage (allumage feux R313-4). Durée minimum : 3h cumulées tous élèves confondus. Après chaque séquence, on fait un débrief individuel.",
      questions_eleve: [
        "Au giratoire, est-ce que tu mets le clignotant en entrant ?",
        "Si une voiture commence à mordre sur ta ligne, qu'est-ce que tu fais ?",
        "Quelle distance laisses-tu avec le scooter devant toi ?",
      ],
      attendu:
        "Tu respectes la signalisation, tu anticipes les usagers vulnérables et tu maintiens une distance de sécurité de 2 secondes.",
    },
    {
      etape: 5,
      duree: '1h',
      titre: 'Séquence 5 — Sensibilisation aux risques (présence parents recommandée)',
      role: 'Échange',
      animation:
        "Je vais traiter avec toi 4 thématiques parmi : (1) Les risques spécifiques aux cyclos et quadricycles, avec des cas d'accidents caractéristiques. (2) La vitesse, le débridage et leurs conséquences sur ta sécurité ET l'environnement. (3) L'alcool, les drogues, les médicaments, la fatigue, la distraction (téléphone, écouteurs). (4) Ton équipement de protection — son efficacité réelle vs ton ressenti. (5) La pression du groupe, le défi, ton image. Je vais te montrer les chiffres ONISR 2024 : les 2-roues motorisés représentent 23 % de la mortalité routière pour moins de 2 % du trafic. 720 tués dont 123 cyclos (+28 vs 2023). Dans ta tranche d'âge 14-17 ans : 40 tués (1 cyclo tué sur 3). Hommes à 94 %. Surmortalité en agglomération.",
      questions_eleve: [
        "Pour toi, c'est plus risqué de rouler en cyclo qu'en voiture ? Pourquoi ?",
        "Comment tu réagirais si un copain te propose de tester son scooter débridé ?",
        "Si tu as un accident, qui paie ?",
      ],
      attendu:
        "À la fin de cette étape, tu SAURAS citer 3 chiffres clés ONISR, tu pourras expliquer pourquoi le débridage = perte d'assurance, et tu sauras simuler un refus argumenté face à un copain.",
    },
    {
      etape: 6,
      duree: '15 min',
      titre: 'Clôture & remise attestation',
      role: 'Animateur',
      animation:
        "On clôture en tour de table : chacun cite UN apprentissage majeur retenu aujourd'hui. Je te remets ton attestation de fin de formation (CERFA réglementaire). Bonne nouvelle : elle est valable IMMÉDIATEMENT pendant 4 mois en attendant la délivrance de ton titre définitif par la préfecture. Tu devras faire ta demande de titre AM en ligne sur ANTS avec : photo, justificatif d'identité, justificatif de domicile, attestation de formation, ASSR1 ou ASR. Tes obligations rappelées : EPI obligatoire à chaque trajet, allumage feux H24, transport de passager possible UNIQUEMENT si ton cyclomoteur est homologué 2 places (carte grise réception « 2 places ») + assurance adaptée, inter-files INTERDITE.",
      questions_eleve: [
        "Qu'est-ce que tu n'oseras PLUS faire après aujourd'hui ?",
        "Quel est le délai max pour faire la demande du titre AM ?",
        "À quelle condition peux-tu prendre un passager sur ton AM L1e ?",
      ],
      attendu:
        "Attestation délivrée, tu sais comment faire la démarche ANTS, tu repars avec ton plan d'action et une vraie prise de conscience.",
    },
  ],

  contenus: [
    {
      id: 'signalisation-types',
      titre: 'Signalisation verticale & horizontale (Séq 3)',
      page_source: 'Notes moniteur — page WA0006',
      points_cles: [
        "Rôle de la signalisation : combattre l'incertitude, renseigner, prescrire",
        "6 types de panneaux : Danger / Interdiction / Obligation / Indication / Direction / Localisation",
        "Forme & couleur — règle visuelle : triangle rouge = danger, rond rouge = interdiction, rond bleu = obligation, carré = indication",
        "Régime de priorité : Forces de l'ordre > Feux > Panneaux STOP/Cédez > Priorité droite > Marquage",
        "Franchissement intersection : règle Gauche-Droite-Gauche",
        "Coussin de sécurité : se décaler légèrement pour gagner du champ visuel",
        "Adapter l'allure à : visibilité, état chaussée, propre capacité de freinage",
      ],
      schema: "Tableau formes/couleurs des 6 catégories de panneaux",
    },
    {
      id: 'commandes-scooter',
      titre: 'Commandes du scooter & exercices plateau (Séq 2)',
      page_source: 'Notes moniteur — page WA0010',
      points_cles: [
        "Commandes : Guidon / Freins (droite avant + gauche arrière sur 2RM standard / INVERSE sur scooter variateur) / Clignotants / Feux / Feux de détresse",
        "Démarrage : kick OU démarreur électrique selon modèle",
        "Position de conduite : tête levée, regard mobile, genoux serrés, 4 doigts sur le levier",
        "Attention à l'effet pince si scooter tombe (doigts coincés)",
        "Accélération : main en continuité avec la poignée, pas cassée",
        "6 exercices imposés : béquillage / démarrage-arrêt / slalom / demi-tour / huit / huit + cône",
        "Règle d'or : la tête tourne avant le guidon",
      ],
    },
    {
      id: 'debridage-consequences',
      titre: 'Débridage : conséquences sécurité, assurance, environnement',
      page_source: 'Notes moniteur — page WA0014',
      points_cles: [
        "Un cyclo est conçu pour ne PAS dépasser 45 km/h (homologation L1e)",
        "1 cyclo accidenté sur 2 est débridé",
        "Débridage 50 cc : +30 % de chances d'accident",
        "Amende : 135 € + immobilisation possible + confiscation possible",
        "En cas d'accident : assurance NE COUVRE PAS les dommages (responsabilité aggravée)",
        "Cyclomotoriste = manque d'expérience + n'a pas passé le Code de la route classique = 2 facteurs de risque",
        "IFSTTAR 2017 : cyclo 2-temps débridé jusqu'à 1000× plus polluant (hydrocarbures) qu'Euro 6",
        "Le céléromètre permet de contrôler la vitesse maxi des L1e limités à 45 km/h",
      ],
    },
    {
      id: 'sensibilisation-risques',
      titre: 'Sensibilisation aux risques — chiffres ONISR 2024 (Séq 5)',
      page_source: 'Notes moniteur — page WA0015',
      points_cles: [
        "Les 2RM représentent 23 % de la mortalité routière pour < 2 % du trafic motorisé",
        "Risque d'être tué × 17 pour un cyclomotoriste vs automobiliste (km parcourus)",
        "20 % des accidents impliquant un cyclo : non-respect du Code par le cyclomotoriste",
        "720 tués 2RM en 2024 dont 123 cyclomotoristes (+28 vs 2023)",
        "Tranches d'âge 14-17 ans = 40 tués / 18-24 ans = 33 tués",
        "Les 14-17 ans : 1 tué sur 3 en cyclomoteur",
        "94 % hommes (vs 75 % pour les autres modes de transport)",
        "Surmortalité en AGGLOMÉRATION pour cyclos et motos légères",
      ],
    },
    {
      id: 'forces-ordre-respect',
      titre: "Posture ECSR — Respect des forces de l'ordre (Séq 3 + Séq 5)",
      page_source: 'Principe pédagogique ECSR transversal',
      points_cles: [
        "Les forces de l'ordre (gendarmerie nationale, police nationale, police municipale) sont en TÊTE de la hiérarchie des priorités routières — avant les feux, panneaux, marquage",
        "À 14 ans, c'est souvent le PREMIER contact avec un contrôle routier — installer dès maintenant les bons réflexes",
        "Ce ne sont pas des adversaires : ce sont les garants du cadre légal qui PROTÈGE le cyclomotoriste (débridage, équipement, alcool, drogues)",
        "Réflexe à l'arrêt : couper le moteur, retirer le casque, mains visibles sur le guidon, rester calme",
        "Documents à présenter : attestation AM + ASSR1 ou ASR + assurance + pièce d'identité (livret tutorial si < 16 ans)",
        "Tutoyer un gendarme/policier en exercice = familiarité interdite (R434-2 outrage)",
        "Outrage à agent : 7 500 € + 6 mois de prison ; rébellion : 1 an + 15 000 € ; refus d'obtempérer : 2 ans + 15 000 €",
        "Message ECSR aux ados : « Si tu es contrôlé et que tu as tort, tais-toi et signe ; si tu as raison, défends-toi calmement et avec respect — JAMAIS d'insulte, JAMAIS de geste »",
        "Séquence 5 (présence parents) : utiliser cette opportunité pour faire passer ce message DEVANT les parents",
      ],
    },
  ],

  evaluation: {
    type: "QCM oral + débrief final (le Permis AM n'a PAS d'examen final classique : c'est la validation des 5 séquences qui ouvre droit à l'attestation)",
    duree: '15 min',
    questions: [
      {
        q: "À quel âge peut-on conduire un cyclomoteur L1e (50 cm³) avec le Permis AM ?",
        choix: ['12 ans', '14 ans révolus', '16 ans', '18 ans'],
        bonne: 1,
        explication:
          "L'âge minimum pour conduire un cyclomoteur L1e (50 cm³) avec le Permis AM est de 14 ans révolus. À 16 ans, on peut également conduire un quadricycle léger L6e (voiturette) avec le même permis. La formation pratique de 8h est obligatoire.",
      },
      {
        q: "Combien de temps l'attestation provisoire de fin de formation est-elle valable en attendant le titre définitif ?",
        choix: ['1 mois', '4 mois', '6 mois', '12 mois'],
        bonne: 1,
        explication:
          "L'attestation de fin de formation est valable IMMÉDIATEMENT pendant 4 mois en attendant la délivrance du titre AM définitif par la préfecture (démarche ANTS). Pendant ces 4 mois, l'élève peut circuler avec son attestation + une pièce d'identité.",
      },
      {
        q: "Quelle est la principale conséquence d'un débridage sur un cyclomoteur ?",
        choix: [
          "Aucune, c'est légal après 16 ans",
          "Amende 135 € + perte de la couverture d'assurance en cas d'accident",
          "Juste une amende symbolique",
          "Une suspension du permis AM",
        ],
        bonne: 1,
        explication:
          "Le débridage entraîne : amende 135 €, immobilisation/confiscation possible, et surtout NON-COUVERTURE par l'assurance en cas d'accident. À noter aussi : +30 % de risque d'accident, et pollution jusqu'à 1000× supérieure (cyclo 2-temps débridé).",
      },
      {
        q: "Au franchissement d'une intersection sans signalisation, dans quel ordre regardes-tu ?",
        choix: [
          "Droite seulement",
          "Gauche-Droite",
          "Gauche-Droite-Gauche",
          "On peut traverser sans regarder si on est prioritaire",
        ],
        bonne: 2,
        explication:
          "La règle est Gauche-Droite-Gauche. On regarde 2 fois à gauche car la priorité droite signifie que les véhicules venant de gauche n'ont pas la priorité, MAIS il faut vérifier qu'ils respectent bien la règle. Le coussin de sécurité (décalage) permet d'élargir le champ visuel.",
      },
      {
        q: "L'inter-files est-elle autorisée pour un cyclomoteur AM (L1e) ?",
        choix: [
          "Oui partout",
          "Oui mais uniquement sur autoroute",
          "NON, jamais",
          "Oui pour les scooters >= 50cc uniquement",
        ],
        bonne: 2,
        explication:
          "L'inter-files (circulation entre files lentes ou à l'arrêt) est INTERDITE pour les cyclomoteurs L1e (Permis AM). Elle n'est autorisée à titre expérimental que pour les motocyclettes ≥ 125 cm³ dans certains départements pilotes. Le cyclo doit rester dans sa file comme tout véhicule.",
      },
    ],
    cas_pratique: {
      enonce:
        "Tu es en scooter, tu arrives à un STOP. Un copain en scooter te dépasse en mode 'fais comme moi, on s'engage sans s'arrêter'. Que fais-tu et que dis-tu ?",
      reponse_attendue:
        "1) Je m'arrête COMPLÈTEMENT au STOP (R415-6 : ligne d'arrêt, pieds posés). 2) Je laisse passer le copain s'il a déjà franchi (sécurité d'abord). 3) Contrôles Gauche-Droite-Gauche, je m'engage seulement si la voie est libre. 4) Au prochain arrêt, j'aborde le copain sans agressivité : « gars, le STOP c'est 135 € + 4 points + ton assurance qui marche plus si t'as un accident — surtout en cas de contrôle des forces de l'ordre (gendarmerie ou police nationale) qui sont très présentes sur les axes 2RM ». 5) Je refuse le défi par la suite et propose un autre comportement (rouler ensemble mais à allure adaptée). 6) Si pression persiste, je change de trajet ou de copain : la pression du groupe est UN des 4 principaux facteurs d'accident en cyclo.",
    },
  },

  references: [
    {
      titre: "Arrêté du 8 novembre 2012 modifié",
      detail:
        "Fixe la formation pratique de 8 heures obligatoire pour l'obtention du Permis AM (catégorie L1e cyclomoteur 50 cm³ ou L6e quadricycle léger). Définit les 5 séquences réglementaires (50 min + 3h + 1h + 3h + 1h).",
    },
    {
      titre: "Arrêté du 14 avril 2014",
      detail:
        "Modifie l'arrêté du 8 novembre 2012 et précise les modalités de mise en œuvre, notamment la délivrance de l'attestation de fin de formation (CERFA) et la valorisation de la séquence 5 (sensibilisation aux risques avec parents).",
    },
    {
      titre: "Article R211-2 du Code de la route",
      detail:
        "Conditions d'accès aux catégories L1e et L6e : âge minimum 14 ans (L1e) ou 16 ans (L6e), formation pratique 8h obligatoire pour les nés après le 1er janvier 1988, possession ASSR1 ou ASR.",
    },
    {
      titre: "Article R311-1 du Code de la route",
      detail:
        "Catégorie L1e (cyclomoteur 2RM) : cylindrée ≤ 50 cm³, vitesse maxi par construction ≤ 45 km/h, puissance ≤ 4 kW. Catégorie L6e (quadricycle léger) : masse à vide ≤ 425 kg, puissance ≤ 6 kW.",
    },
    {
      titre: "Article R317-23 du Code de la route",
      detail:
        "Sanctions pour débridage : amende 135 €, immobilisation possible, confiscation possible. Référence essentielle pour la Séquence 5 — le débridage = perte de l'assurance en cas d'accident.",
    },
    {
      titre: "Article R313-4 du Code de la route",
      detail:
        "Obligation pour les 2RM (dont cyclomoteurs) de circuler avec les feux de croisement allumés en permanence, y compris de jour. Mesure de visibilité.",
    },
    {
      titre: "Article R412-19 du Code de la route",
      detail:
        "Interdiction de l'inter-files (circulation entre files) pour les cyclomoteurs. L'expérimentation inter-files concerne uniquement les 2RM ≥ 125 cm³ dans les départements pilotes.",
    },
    {
      titre: "REMC — Référentiel pour l'Éducation à une Mobilité Citoyenne",
      detail:
        "Compétences C1 (maniement), C2 (conditions normales), C4 (autonomie, sécurité, économie). Niveaux 2-3-4 de la matrice GDE (avec un travail spécifique sur la PERSONNALITÉ à 14 ans : pression de groupe, image, prise de risque).",
    },
    {
      titre: "Données ONISR 2024 / Étude IFSTTAR 2017",
      detail:
        "123 cyclomotoristes tués en 2024 (+28 vs 2023). 14-17 ans : 1 cyclo tué sur 3. 94 % d'hommes. Risque ×17 vs automobiliste (par km). Cyclo 2-temps débridé : +1000× polluant en hydrocarbures vs Euro 6.",
    },
  ],

  conseils_jury: [
    "La formation AM n'a PAS d'examen final classique : c'est la VALIDATION des 5 séquences qui ouvre droit à l'attestation. Tu ne peux pas « rater » mais tu peux être recadré.",
    "L'attestation est valable IMMÉDIATEMENT pendant 4 mois en attendant le titre AM définitif. La démarche se fait sur ANTS (photo, identité, domicile, ASSR1, attestation formation).",
    "Les 14-17 ans sont la population la plus à risque (1 cyclo tué sur 3). La Séquence 5 (présence des parents) est le moment-clé pour faire passer le message ANTI-DÉBRIDAGE et ANTI-PRESSION DE GROUPE.",
    "Vérifier la signature du tuteur sur le livret d'apprentissage DÈS L'ACCUEIL. Sans signature, la formation ne peut pas démarrer.",
    "EPI obligatoire à chaque séquence pratique : casque attaché (pas juste posé), gants homologués, chaussures montantes. Refuser de démarrer si un EPI manque.",
    "Adapter le vocabulaire à l'adolescent : phrases courtes, démonstration AVANT verbalisation, débrief de 2-3 minutes max, valoriser les progrès.",
    "Sur le scooter à variateur : les commandes de frein sont INVERSÉES (gauche = arrière). Bien vérifier que l'élève a compris avant tout exercice plateau.",
    "Ne PAS supprimer la Séquence 5 même si pression horaire : c'est elle qui crée le déclic SAVOIR-ÊTRE qui durera toute la vie de motard.",
  ],
};

export default permisAmBsrCours;
